import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Gift, Trophy, CheckCircle2, Clock, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { Loading, PageHead } from "@/components/common";
import { useAuth } from "@/context/AuthContext";

function prizeText(p) {
  if (!p) return "";
  if (p.type === "cash" && p.value) return `$${Number(p.value).toFixed(0)} cash${p.label ? ` — ${p.label}` : ""}`;
  if (p.type === "product" && p.label) return p.label;
  return p.label || "";
}

const MEDALS = ["🥇", "🥈", "🥉", "🏅", "🏅"];

export default function Invite() {
  const { user } = useAuth();
  const [board, setBoard] = useState(null);
  const [me, setMe] = useState(null);
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => Promise.all([
    api.get("/referrals/leaderboard"),
    api.get("/referrals/me"),
    api.get("/referrals/winners").catch(() => ({ months: [] })),
  ]).then(([b, m, w]) => { setBoard(b); setMe(m); setWinners(w.months || []); })
    .catch(() => {}).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);
  if (loading) return <Loading />;

  const code = me?.code;
  const link = code ? `${window.location.origin}/signup?invite=${code}` : "";
  const prize = prizeText(board?.prize);
  const entries = board?.entries || [];
  const myRank = board?.me?.rank;

  const copy = async (text, label) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied!`);
    } catch (e) {
      try {
        const ta = document.createElement("textarea");
        ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success(`${label} copied!`);
      } catch (e2) {
        toast.error(`Couldn't copy — ${label}: ${text}`);
      }
    }
  };
  const share = async () => {
    const text = "Join me on SouqStream — live shopping from Muslim sellers. Sign up with my link:";
    if (navigator.share) { try { await navigator.share({ title: "SouqStream", text, url: link }); } catch (e) {} }
    else copy(link, "Invite link");
  };

  return (
    <div className="fade-up" data-testid="refer-page">
      <PageHead title="Refer & Win" sub="Invite friends, climb the leaderboard, and win the monthly prize." />

      {/* Prize hero */}
      <div className="ss-card" style={{ padding: 28, background: "var(--grad-hero)", color: "#fff", border: "none" }} data-testid="referral-prize-banner">
        <div className="ss-live-badge"><Trophy size={14} /> {board?.month_label} PRIZE</div>
        {prize ? (
          <>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 34, marginTop: 10 }} data-testid="referral-prize">{prize}</div>
            <p style={{ color: "rgba(255,255,255,.85)", marginTop: 6 }}>
              The <b>top {board?.winners_count || 5}</b> referrers this month win. A referral counts once your friend joins <b>and</b> places an order or hosts a show.
            </p>
          </>
        ) : (
          <>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 26, marginTop: 10 }}>Monthly referral contest</div>
            <p style={{ color: "rgba(255,255,255,.85)", marginTop: 6 }}>
              The top {board?.winners_count || 5} referrers win each month. Invite active friends to climb the board — the prize is announced here.
            </p>
          </>
        )}
      </div>

      {/* My referral link */}
      <div className="ss-card" style={{ padding: 24, marginTop: 20 }} data-testid="my-invite-card">
        <div className="ss-live-badge" style={{ background: "var(--gold-soft)", color: "var(--brand)" }}><Gift size={14} /> YOUR INVITE CODE</div>
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 36, letterSpacing: 2, marginTop: 10 }} data-testid="invite-code">{code || "—"}</div>
        <div style={{ display: "flex", gap: 12, marginTop: 16, flexWrap: "wrap" }}>
          <button className="ss-btn ss-btn-gold" onClick={() => copy(code, "Code")} data-testid="copy-code"><Copy size={16} /> Copy code</button>
          <button className="ss-btn ss-btn-ghost" onClick={() => copy(link, "Invite link")} data-testid="copy-link"><Copy size={16} /> Copy invite link</button>
          <button className="ss-btn ss-btn-primary" onClick={share} data-testid="share-invite"><Sparkles size={16} /> Share</button>
        </div>
      </div>

      {/* My stats */}
      <div className="ss-grid grid-2" style={{ marginTop: 20 }}>
        <div className="ss-card stat-card">
          <div className="stat-ic"><CheckCircle2 size={22} /></div>
          <div><div className="stat-val" data-testid="my-qualified">{me?.qualified_this_cycle || 0}</div><div className="stat-label">Qualified referrals this month</div></div>
        </div>
        <div className="ss-card stat-card">
          <div className="stat-ic"><Trophy size={22} /></div>
          <div><div className="stat-val" data-testid="my-rank">{myRank ? `#${myRank}` : "—"}</div><div className="stat-label">Your rank this month</div></div>
        </div>
      </div>

      {/* Leaderboard */}
      <h2 style={{ fontSize: 20, marginTop: 28, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}><Trophy size={20} style={{ color: "var(--gold-2)" }} /> Leaderboard — {board?.month_label}</h2>
      <div className="ss-card" style={{ overflow: "hidden" }} data-testid="referral-leaderboard">
        {entries.length === 0 ? (
          <div style={{ padding: 26, textAlign: "center", color: "var(--muted)" }}>
            No qualified referrals yet this month — be the first! Share your link above.
          </div>
        ) : (
          <div>
            {entries.map((e) => {
              const isMe = e.user_id === user?.user_id;
              const top = e.rank <= (board?.winners_count || 5);
              return (
                <div key={e.user_id} data-testid={`referral-row-${e.rank}`}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px",
                           borderBottom: "1px solid var(--line)", background: isMe ? "var(--gold-soft)" : (top ? "rgba(196,160,71,.06)" : "transparent") }}>
                  <div style={{ width: 34, textAlign: "center", fontWeight: 800, fontSize: top ? 20 : 15 }}>
                    {top ? MEDALS[e.rank - 1] : e.rank}
                  </div>
                  <img src={e.avatar} alt="" style={{ width: 40, height: 40, borderRadius: 999, objectFit: "cover", border: top ? "2px solid var(--gold)" : "1px solid var(--line)" }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 800 }}>{e.name}{isMe ? " (You)" : ""}</div>
                    {e.username && <div className="ss-muted" style={{ fontSize: 12 }}>@{e.username}</div>}
                  </div>
                  {e.pending > 0 && (
                    <span className="ss-muted" style={{ fontSize: 12, display: "inline-flex", alignItems: "center", gap: 4 }} title="Referred but not active yet"><Clock size={12} /> {e.pending} pending</span>
                  )}
                  <div style={{ fontWeight: 800, color: "var(--brand)", fontSize: 18, minWidth: 44, textAlign: "right" }}>{e.count}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <p className="ss-muted" style={{ fontSize: 12, marginTop: 8 }}>
        Winners are locked in when the month closes. Prizes (cash or product) are arranged with you directly.
      </p>

      {/* Hall of fame */}
      {winners.length > 0 && (
        <>
          <h2 style={{ fontSize: 20, marginTop: 28, marginBottom: 12 }}>🏆 Past winners</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }} data-testid="referral-hall-of-fame">
            {winners.map((m) => (
              <div key={m.id} className="ss-card" style={{ padding: 16 }}>
                <div style={{ fontWeight: 800, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                  <span>{m.month_label}</span>
                  <span className="ss-muted" style={{ fontWeight: 600, fontSize: 13 }}>{prizeText(m.prize) || "No prize"}</span>
                </div>
                <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 12 }}>
                  {(m.winners || []).length === 0 && <span className="ss-muted" style={{ fontSize: 13 }}>No winners recorded</span>}
                  {(m.winners || []).map((w) => (
                    <span key={w.user_id} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14 }}>
                      <span style={{ fontSize: 16 }}>{MEDALS[w.rank - 1] || `#${w.rank}`}</span>
                      <b>{w.name}</b><span className="ss-muted">({w.count})</span>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
